import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar as MuiSnackbar, Alert, AlertTitle, Slide } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

const SnackbarContext = createContext();
export const useSnackbar = () => useContext(SnackbarContext);

function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

// Axios surfaces connectivity failures with raw strings like "Network Error" or
// our own legacy fallback "Network Failed!". These reach the user verbatim when
// catch blocks pass error.message into a snackbar. Rewrite them into a clear
// sentence the student / trainer can actually act on.
const NETWORK_FALLBACK_MESSAGE =
  "Unable to reach the server. Please check your internet connection and try again.";
const RAW_NETWORK_STRINGS = new Set([
  "Network Error",
  "network error",
  "Network Failed",
  "Network Failed!",
  "ERR_NETWORK",
  "Failed to fetch",
]);
const humanizeNetworkMessage = (msg) => {
  if (msg == null) return NETWORK_FALLBACK_MESSAGE;
  const str = String(msg).trim();
  if (!str) return NETWORK_FALLBACK_MESSAGE;
  if (RAW_NETWORK_STRINGS.has(str)) return NETWORK_FALLBACK_MESSAGE;
  // Catch axios timeout strings too: "timeout of 300000ms exceeded".
  if (/^timeout of \d+ms exceeded$/i.test(str)) {
    return "The server took too long to respond. Please try again.";
  }
  return str;
};

export const SnackbarProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("info");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [autoHideDuration, setAutoHideDuration] = useState(2000); // 👈 default

  // ✅ duration is optional
  const showSnackbar = useCallback(
    (severity, message, title = "", duration = 2000) => {
      const finalMessage =
        severity === "error" ? humanizeNetworkMessage(message) : message;
      setSeverity(severity);
      setMessage(finalMessage);
      setTitle(title);
      setAutoHideDuration(duration); // 👈 set duration
      setOpen(true);
    },
    []
  );

  const handleClose = () => setOpen(false);

  const iconMap = {
    success: <CheckCircleIcon fontSize="inherit" />,
    error: <ErrorIcon fontSize="inherit" />,
    info: <InfoIcon fontSize="inherit" />,
    warning: <WarningIcon fontSize="inherit" />,
  };

  const bgColorMap = {
    success: "#d4edda",
    error: "#f8d7da",
    info: "#d1ecf1",
    warning: "#fff3cd",
  };

  const textColorMap = {
    success: "#155724",
    error: "#721c24",
    info: "#0c5460",
    warning: "#856404",
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <MuiSnackbar
        open={open}
        autoHideDuration={autoHideDuration} // ✅ dynamic
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={SlideTransition}
        sx={{ zIndex: 999999 }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          icon={iconMap[severity]}
          sx={{
            minWidth: 300,
            borderRadius: "12px",
            boxShadow: "0px 6px 18px rgba(0,0,0,0.25)",
            fontSize: "0.95rem",
            backgroundColor: bgColorMap[severity],
            color: textColorMap[severity],
          }}
        >
          {title && <AlertTitle>{title}</AlertTitle>}
          {message}
        </Alert>
      </MuiSnackbar>
    </SnackbarContext.Provider>
  );
};
